import { useQuery } from '@tanstack/react-query';
import { fetchProblems } from '@/lib/api';
import { Link } from 'react-router-dom';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bug, AlertTriangle, BookOpen, Link2, Clock } from 'lucide-react';

export default function ProblemsPage() {
  const { data: problems, isLoading } = useQuery({
    queryKey: ['problems'],
    queryFn: fetchProblems,
    refetchInterval: 15000,
  });

  const withKedb = (problems || []).filter(p => p.kedb_entry);
  const withoutKedb = (problems || []).filter(p => !p.kedb_entry);

  return (
    <div data-testid="problems-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Problem Management</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">NOVA agent identifies patterns and root causes \u2022 {(problems || []).length} problems tracked</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Problem list */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-[var(--nexus-bg-elevated)] animate-pulse" />)}</div>
          ) : (
            <div className="space-y-3" data-testid="problems-list">
              {(problems || []).map(problem => (
                <div key={problem.id} className="nexus-card relative overflow-hidden p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-info)] bg-opacity-10">
                        <Bug size={18} className="text-[var(--nexus-info)]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Link to={`/incidents/${problem.id}`} className="font-mono text-xs text-[var(--nexus-primary-bright)] hover:underline">{problem.id}</Link>
                          <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded badge-${problem.priority?.toLowerCase()}`}>{problem.priority}</span>
                          <StatusDot status={problem.status === 'RESOLVED' ? 'success' : 'warning'} pulse={problem.status !== 'RESOLVED'} size="sm" />
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">{problem.status}</span>
                        </div>
                        <p className="text-sm text-[var(--nexus-text-primary)] mt-1">{problem.title}</p>
                        
                        {/* Pattern strength */}
                        {problem.pattern_strength && (
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[10px] text-[var(--nexus-text-muted)]">Pattern:</span>
                            <div className="w-20 h-1.5 rounded-full bg-[var(--nexus-bg-base)] overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--nexus-info)] transition-all" style={{ width: `${problem.pattern_strength}%` }} />
                            </div>
                            <span className="font-mono text-[10px] text-[var(--nexus-info)] tabular-nums">{problem.pattern_strength}%</span>
                          </div>
                        )}
                        
                        {/* Linked incidents */}
                        {problem.linked_incidents?.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <Link2 size={10} className="text-[var(--nexus-text-muted)]" />
                            <span className="text-[10px] text-[var(--nexus-text-muted)]">{problem.linked_incidents.length} linked incidents</span>
                          </div>
                        )}

                        {/* Root cause */}
                        {problem.root_cause && (
                          <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                            <AlertTriangle size={10} className="text-[var(--nexus-warning)] flex-shrink-0" />
                            <span className="text-[10px] font-mono text-[var(--nexus-warning)]">{problem.root_cause}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-3 mt-2">
                          <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{problem.assigned_agent}</span>
                        </div>
                      </div>
                    </div>
                    {problem.confidence && (
                      <div className="text-right">
                        <span className="font-mono text-lg font-semibold text-[var(--nexus-info)] tabular-nums">{problem.confidence}%</span>
                        <p className="text-[9px] text-[var(--nexus-text-muted)]">confidence</p>
                      </div>
                    )}
                  </div>

                  {/* KEDB Entry (if exists) */}
                  {problem.kedb_entry && (
                    <div className="mt-3 p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]" data-testid={`kedb-${problem.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen size={12} className="text-[var(--nexus-success)]" />
                        <span className="text-[10px] uppercase tracking-wider text-[var(--nexus-success)] font-medium">Known Error Database Entry</span>
                      </div>
                      {problem.kedb_entry.symptoms?.length > 0 && (
                        <div className="mb-1.5">
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">Symptoms: </span>
                          <span className="text-[10px] text-[var(--nexus-text-secondary)]">{problem.kedb_entry.symptoms.join(', ')}</span>
                        </div>
                      )}
                      {problem.kedb_entry.workaround && (
                        <div className="mb-1.5">
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">Workaround: </span>
                          <span className="text-[10px] text-[var(--nexus-text-secondary)]">{problem.kedb_entry.workaround}</span>
                        </div>
                      )}
                      {problem.kedb_entry.recommended_fix && (
                        <div>
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">Recommended Fix: </span>
                          <span className="text-[10px] text-[var(--nexus-accent)]">{problem.kedb_entry.recommended_fix}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {(!problems || problems.length === 0) && (
                <div className="nexus-card p-12 text-center">
                  <Bug size={32} className="text-[var(--nexus-text-muted)] mx-auto mb-3" />
                  <p className="text-sm text-[var(--nexus-text-muted)]">No problems identified</p>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Trigger the "Problem: Pattern Detect" scenario from Settings to have NOVA analyze incidents</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right column: KEDB Summary + Stats */}
        <div className="space-y-3">
          {/* KEDB Summary */}
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <BookOpen size={14} className="text-[var(--nexus-success)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Known Error Database</h2>
              </div>
            </div>
            <div className="p-3">
              <div className="space-y-2">
                <div className="flex justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <span className="text-[11px] text-[var(--nexus-text-muted)]">KEDB Entries</span>
                  <span className="font-mono text-xs text-[var(--nexus-success)] tabular-nums">{withKedb.length}</span>
                </div>
                <div className="flex justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <span className="text-[11px] text-[var(--nexus-text-muted)]">Pending Investigation</span>
                  <span className="font-mono text-xs text-[var(--nexus-warning)] tabular-nums">{withoutKedb.length}</span>
                </div>
                <div className="flex justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <span className="text-[11px] text-[var(--nexus-text-muted)]">Total Problems</span>
                  <span className="font-mono text-xs text-[var(--nexus-text-primary)] tabular-nums">{(problems || []).length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Problem Lifecycle */}
          <div className="nexus-card relative overflow-hidden p-3">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Problem Lifecycle</h3>
            <div className="space-y-1">
              {['DETECTED', 'UNDER_INVESTIGATION', 'KNOWN_ERROR', 'FIX_IN_PROGRESS', 'RESOLVED'].map((status, i) => (
                <div key={status} className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${i <= 1 ? 'bg-[var(--nexus-warning)]' : i <= 3 ? 'bg-[var(--nexus-accent)]' : 'bg-[var(--nexus-success)]'}`} />
                  <span className="text-[10px] text-[var(--nexus-text-secondary)]">{status.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* How to trigger */}
          <div className="nexus-card relative overflow-hidden p-3">
            <h3 className="text-xs font-semibold text-[var(--nexus-text-muted)] mb-2">How to generate problems</h3>
            <p className="text-[10px] text-[var(--nexus-text-muted)] leading-relaxed">
              Go to <Link to="/settings" className="text-[var(--nexus-accent)] hover:underline">Settings</Link> and trigger the "Problem: Pattern Detect" scenario. NOVA will analyze closed incidents and create problem records with KEDB entries.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
